import type { ReactNode } from 'react';

import { defineMessage, defineMessages } from 'react-intl';

import { getOverageRateTiers } from '../calculator/overage-cost';

import type { IconNames } from '../../../../uniform/components/text-with-icon/src/icon-mapping';
import type { Meter } from '../context/types';

export type BulletOrIconListItem = {
	icon?: IconNames;
	text: string | ReactNode;
};

export interface FormatPriceOptions {
	currency: string;
	locale: string;
	fractionDigits?: number;
}

/**
 * Customer-facing meter display names.
 *
 * Source of truth: per-meter Confluence content specs (each spec has a
 * "Meter name" row on the Step 1 table). Any change to these strings
 * must be signed off by the meter's Content DRI.
 *
 * NOTE: These are the DISPLAY labels shown to users. The internal enum
 * key (`Meter`) is kept as a stable identifier used in URLs, analytics,
 * and context state — do NOT rename the keys.
 */
export const METER_LABEL_MESSAGES = defineMessages<Meter>({
	rovoCredits: {
		id: 'ubp.calculator.meterLabel.rovoCredits',
		defaultMessage: 'Rovo credits',
	},
	automations: {
		id: 'ubp.calculator.meterLabel.automations',
		defaultMessage: 'Automation steps',
	},
	csResolutions: {
		id: 'ubp.calculator.meterLabel.csResolutions',
		defaultMessage: 'AI agent resolutions',
	},
	assetsObjects: {
		id: 'ubp.calculator.meterLabel.assetsObjects',
		defaultMessage: 'Assets objects',
	},
});

/**
 * Included-allowance line on the collection product cards, e.g.
 * "150,000 Rovo credits". The count is pre-formatted for the user's locale by
 * the caller and passed in as `{allowance}`.
 */
export const collectionAllowanceMessage = defineMessage({
	id: 'ubp.calculator.productcard.collectionAllowance',
	defaultMessage: '{allowance} {meterLabel}',
});

/**
 * Formats a large integer for display (e.g. usage / allowance counts):
 * 145000 → "145,000" (using the caller's locale).
 */
export const formatNumber = (value: number, locale: string): string =>
	new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value);

export const formatPrice = (price: number, options: FormatPriceOptions): string => {
	const { currency, locale, fractionDigits = 0 } = options;
	return new Intl.NumberFormat(locale, {
		style: 'currency',
		currency,
		minimumFractionDigits: fractionDigits,
		maximumFractionDigits: fractionDigits,
	}).format(price);
};

/**
 * Formats a low/high pair as a range, collapsing to a single number when the
 * bounds are equal (e.g. a custom usage override makes low === high). Keeps
 * the en-dash range otherwise: "500–2,000".
 */
export const formatNumberRange = (low: number, high: number, locale: string): string =>
	low === high
		? formatNumber(low, locale)
		: `${formatNumber(low, locale)}–${formatNumber(high, locale)}`;

/**
 * Price variant of {@link formatNumberRange}. Preserves the display shape of
 * the overage-cost headline: currency prefix, priced low bound, then a
 * bare-number high bound to avoid repeating the currency symbol. Collapses to
 * a single priced value when the bounds are equal.
 */
export const formatPriceRange = (low: number, high: number, options: FormatPriceOptions): string =>
	low === high
		? `${options.currency} ${formatPrice(low, options)}`
		: `${options.currency} ${formatPrice(low, options)}–${formatNumber(high, options.locale)}`;

export interface BuildOverageRateValuesArgs {
	meter: Meter;
	currency: string;
	locale: string;
}

/**
 * ICU values for a meter's `rateNote` copy, keyed `rate`, `rate2`, `rate3`, …
 * one per overage rate tier. Shared by the Estimate and Summary steps so both
 * render the same per-unit rate line. Sub-dollar rates keep two fraction digits
 * so "$0.50" doesn't round to "$1".
 */
export const buildOverageRateValues = (
	args: BuildOverageRateValuesArgs,
): Record<string, string> => {
	const { meter, currency, locale } = args;

	return getOverageRateTiers(meter).reduce<Record<string, string>>((values, tier, index) => {
		values[index === 0 ? 'rate' : `rate${index + 1}`] = `${currency} ${formatPrice(tier.price, {
			currency,
			locale,
			fractionDigits: Number.isInteger(tier.price) ? 0 : 2,
		})}`;
		return values;
	}, {});
};
